import { ChatService } from '@apparel/core/chat';
import { Chat } from '@apparel/core/chat/Chat';
import { unix } from '@apparel/core/date-time';
import { Log } from '@apparel/core/log';
import { ChatMessage } from '@apparel/core/models/ChatMessage';
import { ChatMessageStatus } from '@apparel/core/models/ChatMessageStatus';
import { ChatRoom } from '@apparel/core/models/ChatRoom';
import { List, Map, Record as Rec } from 'immutable';
import React from 'react';

const MAX_RETENTION_TIME = 5000;

export interface ChatAction {
    type: keyof ChatState;
    payload: any;
    rest: any[];
}

interface RoomMessages {
    new: List<ChatMessage>;
    list: List<ChatMessage>;
    last_message_id: number;
    room: ChatRoom;
    pagination: {
        first_message_id: number;
        totals: number;
    };
}

export interface ChatStateType {
    connected: boolean;
    room_id: number | null;
    room: ChatRoom | null;
    loading: Record<'rooms' | 'messages' | 'send', boolean>;
    errors: Record<'rooms' | 'messages' | 'send', Error | null>;
    messages: Map<number, RoomMessages>;
    rooms: List<ChatRoom>;
}

export const ChatStateRecord = Rec<ChatStateType>({
    connected: false,
    room_id: null,
    room: null,
    messages: Map(),
    rooms: List(),

    loading: {
        messages: false,
        rooms: false,
        send: false,
    },

    errors: {
        messages: null,
        rooms: null,
        send: null,
    },
});

let mCounter = Math.floor(new Date().getTime() / 1000);
let chatDispatcher: React.Dispatch<ChatAction> | null = null;
const chatDispatcherQueue: any[] = [];

export class ChatState extends ChatStateRecord {
    get logger() {
        return Log.channel('ChatState');
    }

    public dispatch<K extends keyof ChatState>(
        action: K,
        payload?: ChatState[K] extends (payload?: infer P, ...args: any) => any ? P : ChatState[K],
        ...rest: ChatState[K] extends (payload?: any, ...args: infer P) => any ? P : any
    ) {
        const action$ = {
            type: action,
            payload,
            rest,
        };

        if (chatDispatcher) {
            chatDispatcher(action$);
        } else {
            chatDispatcherQueue.push(action$);
        }
    }

    public setDispatcher(dispatcher: React.Dispatch<ChatAction> | null) {
        const runQueue = !chatDispatcher;
        chatDispatcher = dispatcher;

        if (runQueue) {
            while (chatDispatcherQueue.length > 0) {
                this.dispatch(chatDispatcherQueue.pop());
            }
        }
    }

    public setConnected(state: boolean) {
        return this.set('connected', state);
    }

    public updateState(state: Partial<ChatStateType>) {
        return this.mergeDeep(state);
    }

    public removeMessageFromNewList(message: ChatMessage | Record<string, any>) {
        const message$ = new ChatMessage().from(message);

        const list = this.getNewMessagesOfRoomId(message$.room_id).filter((value) => value.id !== message$.id);

        return this.setNewMessagesOfRoomId(message.room_id, list);
    }

    public appendMessageToList(prop: 'new' | 'list', message: ChatMessage, roomId?: number | null, replaceId?: number) {
        const roomId$ = message.room_id ?? roomId;
        const key = ['messages', roomId$, prop];
        const message$ = new ChatMessage().from(message);

        let list: List<ChatMessage> = this.getIn(key) || List<ChatMessage>();
        let rooms = this.get('rooms');

        if (replaceId) {
            list = list.filter((item: ChatMessage) => item.id !== replaceId);
        }

        list = list.push(message$);

        /**
         * Update last message of the room's message
         */
        rooms = rooms.map((room: ChatRoom) => {
            if (room.id === roomId) {
                room.last_message = message$;
            }
            return room;
        });

        list = list
            .groupBy((item) => item.id)
            .map<ChatMessage>((item) => item.first())
            .toList()
            .sortBy<number>((item: ChatMessage) => unix(item.created_at));

        return this.set('rooms', rooms).setIn(key, list);
    }

    public addNewMessage(message: ChatMessage | Record<string, any>) {
        const message$ = message instanceof ChatMessage ? message : new ChatMessage().from(message);
        const roomId = message$.room_id;

        this.dispatch('appendMessageToList', 'new', message$, roomId);
        this.dispatch('appendMessageToList', 'list', message$, roomId);

        setTimeout(() => this.dispatch('removeMessageFromNewList', message$), MAX_RETENTION_TIME);
    }

    public setRoomTyping(roomId: number, typing: boolean) {
        this.logger.debug(`Set typing [${typing}] for room [${roomId}]`);
        ChatService.getInstance().sendUserTyping(roomId, typing);
    }

    public updateMessage(message: ChatMessage | Record<string, any>) {
        const message$ = message instanceof ChatMessage ? message : new ChatMessage().from(message);
        const roomId = message$.room_id;
        this.dispatch('appendMessageToList', 'list', message$, roomId, message$.id);
        return this;
    }

    /**
     * Set new messages of room
     * @param roomId
     * @param list
     */
    public setNewMessagesOfRoomId(roomId: number, list: List<ChatMessage>) {
        return this.setIn(['messages', roomId, 'new'], list);
    }

    /**
     * Get new messages of room id
     * @param roomId
     */
    public getNewMessagesOfRoomId(roomId: number): List<ChatMessage> {
        return this.getIn(['messages', roomId, 'new']) || List();
    }

    /**
     * Get new messages of active room
     */
    public getNewMessagesOfActiveRoom(): List<ChatMessage> {
        return this.getNewMessagesOfRoomId(this.get('room_id')!);
    }

    /**
     * Count new messages of active room
     */
    public countNewMessagesOfActiveRoom() {
        return this.getNewMessagesOfActiveRoom()?.size || 0;
    }

    /**
     * Set new messages of room
     * @param roomId
     * @param list
     */
    public setMessagesOfRoomId(roomId: number, list: List<ChatMessage>) {
        return this.setIn(['messages', roomId, 'list'], list);
    }

    /**
     * Get messages of room id
     * @param roomId
     */
    public getMessagesOfRoomId(roomId: number): List<ChatMessage> {
        return this.getIn(['messages', roomId, 'list']) || List();
    }

    /**
     * Get messages of active room
     */
    public getMessagesOfActiveRoom(): List<ChatMessage> {
        return this.getMessagesOfRoomId(this.get('room_id')!);
    }

    /**
     * Count messages of active room
     */
    public countMessagesOfActiveRoom() {
        return this.getMessagesOfActiveRoom()?.size || 0;
    }

    private roomMessagesKey(roomId: number, prop: string) {
        return ['messages', roomId, prop];
    }

    private mapMessages(messages: (ChatMessage | Record<string, any>)[]) {
        return (messages ?? []).reduce((current: List<ChatMessage>, message: ChatMessage) => {
            return current.push(new ChatMessage().from(message));
        }, List<ChatMessage>());
    }

    public populateMessagesOfRoomId(roomId: number, { data, pagination }: any = {}) {
        return this.setLoading('messages', false)
            .setIn(this.roomMessagesKey(roomId, 'list'), this.mapMessages(data?.messages))
            .setIn(this.roomMessagesKey(roomId, 'last_message_id'), data?.last_message_id)
            .setIn(this.roomMessagesKey(roomId, 'room'), new ChatRoom().from(data?.room))
            .setIn(this.roomMessagesKey(roomId, 'pagination'), pagination);
    }

    public loadMessagesOfRoomId(roomId: number) {
        return this.setLoading('messages', true).async(async () => {
            console.log('roomId', roomId);
            if (roomId) {
                try {
                    const { data } = await Chat.getMessagesByRoomID(roomId);
                    this.dispatch('populateMessagesOfRoomId', roomId, data);
                } catch (e) {
                    this.dispatch('setError', 'messages', e);
                    this.dispatch('setLoading', 'messages', false);
                }
            }
        });
    }

    public loadMessagesOfActiveRoom() {
        return this.loadMessagesOfRoomId(this.get('room_id')!);
    }

    public populateRooms(data: ChatRoom[]) {
        const list = (data || []).reduce((current, room) => current.push(new ChatRoom().from(room)), List<ChatRoom>());

        return this.setLoading('rooms', false).set('rooms', list);
    }

    public loadRooms() {
        return this.setLoading('rooms', true).async(async () => {
            try {
                const data = await Chat.getRooms();
                this.dispatch('populateRooms', data);
            } catch (e) {
                this.dispatch('setError', 'rooms', e);
                this.dispatch('setLoading', 'rooms', false);
            }
        });
    }

    public countRooms() {
        return this.getRooms()?.size || 0;
    }

    public getRooms() {
        return this.get('rooms') || List();
    }

    public sendMessage(message: { message: ChatMessage['message'] } & Partial<ChatMessage>) {
        message.message = `${message.message}`.trim();
        if (!message.message) {
            return this;
        }

        return this.setLoading('send', true).async(async () => {
            const mId = ++mCounter;
            const message$ = new ChatMessage()
                .from({
                    id: message.id ?? mId,
                    created_at: new Date(),
                    ...message,
                })
                .sending();

            this.dispatch('appendMessageToList', 'list', message$);

            try {
                const data = await Chat.postMessage(message);
                this.dispatch('appendMessageToList', 'list', data, null, message$.id);
            } catch (e) {
                this.dispatch('setError', 'send', e);

                const rId = message$.id;
                mCounter = new Date().getTime();
                message$.id = ++mCounter;

                this.dispatch('appendMessageToList', 'list', message$.failToSend(), null, rId);
            }

            this.dispatch('setLoading', 'send', false);
        });
    }

    public retrySend(message: { message: ChatMessage['message'] } & Partial<ChatMessage>) {
        this.dispatch('sendMessage', message);
    }

    public setLoading(prop: keyof ChatStateType['loading'], state: boolean) {
        return this.setIn(['loading', prop], state);
    }

    public setError(prop: keyof ChatStateType['errors'], state: Error | null) {
        return this.setIn(['errors', prop], state);
    }

    public getError(prop: keyof ChatStateType['errors']) {
        return this.getIn(['errors', prop]);
    }

    public readMessage({ messageId, userId, roomId }: { messageId: number; roomId: number; userId?: number }) {
        Chat.readMessageOfRoom(messageId, roomId);

        let messages = this.getMessagesOfRoomId(roomId);
        const rooms = this.getRooms().map((room) => {
            if (room.id === roomId) {
                const room$ = new ChatRoom().from(room);
                room$.unread_messages = Math.max(0, room$.unread_messages - 1);
                console.log('decrease room', room$.id, room$.unread_messages);
                return room$;
            }

            return room;
        });

        if (userId) {
            messages = messages.map((message) => {
                if (message.id === messageId) {
                    message.statuses = message.statuses.map((status) => {
                        if (status.user_id === userId) {
                            return status.addStatusFlag(ChatMessageStatus.READ);
                        }

                        return status;
                    });
                }

                return message;
            });
        }

        return this.setMessagesOfRoomId(roomId, messages).set('rooms', rooms);
    }

    private async(runner: () => Promise<void>) {
        setTimeout(() => {
            // noinspection JSIgnoredPromiseFromCall
            runner();
        }, 1);

        return this;
    }

    public senderTyping(data: any) {
        return this.set(
            'rooms',
            this.getRooms().map((room) => {
                if (room.id === data.room_id) {
                    const room$ = new ChatRoom().from(room);
                    room$.typing_members = room$.typing_members ?? [];
                    room$.typing_members = room$.typing_members.filter(({ sender_id }) => sender_id !== data.sender_id);

                    if (data.typing) {
                        room$.typing_members = [...room$.typing_members, data];
                    }

                    return room$;
                }

                return room;
            }),
        );
    }

    public addUnreadForRoom(roomId) {
        return this.set(
            'rooms',
            this.getRooms().map((room) => {
                if (room.id === roomId) {
                    const room$ = new ChatRoom().from(room);
                    room$.unread_messages += 1;
                    console.log('increase room', room$.id, room$.unread_messages);
                    return room$;
                }

                return room;
            }),
        );
    }

    public getUnreadMessages() {
        return this.getRooms().reduce((accum, { unread_messages }) => accum + unread_messages, 0);
    }

    public getRoom(roomId: any): ChatRoom | null {
        return this.getRooms().find(({ id }) => id === roomId) || null;
    }

    public getTypingMembersOfRoom(roomId: number) {
        const room = this.get('rooms').find((room) => room.id === roomId);
        const ids = (room?.typing_members || []).map(({ sender_id }) => sender_id);
        return (room?.members || []).filter(({ id }) => ids.includes(id));
    }
}
